/**
 * Shared Precision Storytelling module experience.
 * Each of the five page components (OriginCut, TurningPoint, …) is a thin
 * wrapper that renders <StoryModule slug="…" />, exactly as Copywriting shares
 * CopyEditor/CopyScoreDisplay across its five module pages.
 *
 * Real content (Module 1 = Origin Cut) renders the full editing exercise:
 * source material → editor with live word count → AI score on the 5-axis rubric.
 * Placeholder modules render a "coming soon" state (content still being written).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useStorytellingScoring } from '../hooks/useStorytellingScoring';
import { getExercisesByModule } from '../data/storytellingExercises.js';
import { moduleForSlug, SKILL_ROUTE } from '../modules.js';

const ACCENT = '#db2777';
const AXIS_LABELS = {
  structuralCompleteness: 'Structural completeness',
  economy: 'Economy',
  specificity: 'Specificity',
  payoff: 'Payoff',
  audienceFit: 'Audience fit',
};

const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);

const StoryModule = ({ slug }) => {
  const navigate = useNavigate();
  const mod = moduleForSlug(slug);
  const exercise = getExercisesByModule(slug)[0] ?? null;

  usePageTitle(`${mod?.title ?? 'Storytelling'} | Castle Atlas`);

  const { scoreExercise, loading } = useStorytellingScoring();
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const words = useMemo(() => wordCount(text), [text]);

  const handleSubmit = async () => {
    if (!text.trim() || loading) return;
    setError(null);
    try {
      const res = await scoreExercise({
        moduleId: slug,
        exerciseOrder: exercise?.order ?? 1,
        userText: text.trim(),
        sourceMaterial: exercise?.sourceMaterial ?? '',
        instruction: exercise?.instruction ?? '',
        goal: exercise?.goal ?? '',
        scoringCriteria: exercise?.scoringCriteria ?? [],
      });
      setResult(res);
    } catch (err) {
      setError(err.message || 'Scoring failed. Please try again.');
    }
  };

  const BackBar = (
    <div style={{
      position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 20px', background: 'hsl(var(--background))', borderBottom: '1px solid hsl(var(--border))',
    }}>
      <button onClick={() => navigate(SKILL_ROUTE)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, color: 'hsl(var(--foreground))' }}>
        <ArrowLeft size={18} />
      </button>
      <span style={{ fontFamily: 'var(--font-skill-story)', fontSize: 15, fontWeight: 800, color: 'hsl(var(--foreground))' }}>{mod?.title}</span>
    </div>
  );

  // ── Placeholder modules (2–5): content still being written ──
  if (!exercise || exercise.placeholder) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <div className="rail-clearance">
        {BackBar}
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '64px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✍️</div>
          <h2 style={{ fontFamily: 'var(--font-skill-story)', fontSize: 26, fontWeight: 800, color: 'hsl(var(--foreground))', margin: '0 0 10px' }}>{mod?.title}</h2>
          <p style={{ fontSize: 15, color: 'hsl(var(--muted-foreground))', lineHeight: 1.6, margin: '0 0 24px' }}>
            This module's exercises are being written. The Origin Cut is live now — start there.
          </p>
          <button onClick={() => navigate(`${SKILL_ROUTE}/origin-cut`)} style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: ACCENT, color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
            Go to The Origin Cut
          </button>
        </div>
        </div>
      </div>
    );
  }

  const over = exercise.tags?.includes('Beginner') && words > 100; // soft cue for the 100-word target

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      <div className="rail-clearance">
      {BackBar}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 80px' }}>
        {/* Source material */}
        {exercise.sourceMaterial && (
          <div style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', borderRadius: 16, padding: '18px 20px', marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT, marginBottom: 10 }}>Raw material</div>
            <p style={{ fontSize: 14, color: 'hsl(var(--foreground))', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{exercise.sourceMaterial}</p>
          </div>
        )}

        {/* Task */}
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontFamily: 'var(--font-skill-story)', fontSize: 20, fontWeight: 800, color: 'hsl(var(--foreground))', margin: '0 0 8px' }}>{exercise.title}</h2>
          <p style={{ fontSize: 14, color: 'hsl(var(--foreground))', lineHeight: 1.6, margin: '0 0 10px' }}>{exercise.instruction}</p>
          {exercise.goal && <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>{exercise.goal}</p>}
        </div>

        {/* Editor */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'hsl(var(--muted-foreground))' }}>Your cut</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: over ? '#ef4444' : 'hsl(var(--muted-foreground))' }}>{words} words</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Cut it down here…"
            rows={7}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 14,
              border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))', fontSize: 15, lineHeight: 1.6, resize: 'vertical', fontFamily: 'var(--font-body)',
            }}
          />
          {exercise.hint && (
            <p style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', margin: '8px 0 0', lineHeight: 1.5 }}>
              <strong style={{ color: ACCENT }}>Hint:</strong> {exercise.hint}
            </p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!text.trim() || loading}
          style={{
            width: '100%', padding: '14px 24px', borderRadius: 12, border: 'none',
            background: !text.trim() || loading ? 'hsl(var(--muted))' : ACCENT,
            color: !text.trim() || loading ? 'hsl(var(--muted-foreground))' : '#fff',
            fontSize: 14, fontWeight: 800, cursor: !text.trim() || loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {loading ? <><Loader2 size={15} className="spin" /> Scoring…</> : <><Sparkles size={15} /> Score my cut</>}
        </button>

        {error && <p style={{ fontSize: 13, color: '#ef4444', marginTop: 12 }}>{error}</p>}

        {/* Score */}
        {result?.scores && (
          <div style={{ marginTop: 22, background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', borderRadius: 16, padding: '20px 22px' }}>
            <style>{`.spin{animation:spin 0.8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 40, fontWeight: 800, color: ACCENT, lineHeight: 1 }}>{result.scores.overall}</span>
              <span style={{ fontSize: 15, color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>/100</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: result.scores.overall >= 60 ? '#22c55e' : '#ef4444' }}>
                {result.scores.overall >= 60 ? 'Passed — progress saved' : 'Below 60 — cut harder'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              {Object.entries(AXIS_LABELS).map(([key, label]) => (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'hsl(var(--muted-foreground))' }}>{label}</span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: 'hsl(var(--foreground))' }}>{result.scores[key]}</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 99, background: 'hsl(var(--border))', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${result.scores[key]}%`, background: ACCENT, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              ))}
            </div>
            {result.feedback && (
              <p style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'hsl(var(--background))', fontSize: 13, color: 'hsl(var(--foreground))', lineHeight: 1.6, fontStyle: 'italic' }}>
                “{result.feedback}”
              </p>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default StoryModule;
